import { useMemo } from 'react';
import { useQueries, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { publicPropertyResource } from '@/lib/query/resources/publicProperties';
import { createSupabaseChannelSubscribe } from '@/lib/query/resourceFactory';
import { supabase } from '@/lib/supabase/client';
import type { GetPublicPropertyParams } from '@/lib/supabase/publicProperties';
import type {
  PublicPropertyDetail,
  PublicPropertyFilterState,
  PublicPropertyListResult,
} from '@/lib/types/publicProperty';

export interface UsePublicPropertyListOptions {
  page?: number;
  pageSize?: number;
  filters?: PublicPropertyFilterState;
  vacancyOnly?: boolean;
}

export function usePublicPropertyList(
  options: UsePublicPropertyListOptions = {},
): UseQueryResult<PublicPropertyListResult> {
  const { page, pageSize, filters, vacancyOnly } = options;

  return publicPropertyResource.useList({ page, pageSize, filters, vacancyOnly });
}

export function useAllPublicPropertyList(
  options: UsePublicPropertyListOptions = {},
): Pick<UseQueryResult<PublicPropertyListResult>, 'data' | 'isLoading' | 'isFetching' | 'error'> {
  const { pageSize, filters, vacancyOnly } = options;

  const firstPageQuery = usePublicPropertyList({
    page: 1,
    pageSize,
    filters,
    vacancyOnly,
  });

  const totalPages = firstPageQuery.data?.totalPages ?? 1;

  const remainingPageParams = useMemo(() => {
    if (totalPages <= 1) {
      return [];
    }

    return Array.from({ length: totalPages - 1 }, (_, index) => ({
      page: index + 2,
      pageSize,
      filters,
      vacancyOnly,
    }));
  }, [filters, pageSize, totalPages, vacancyOnly]);

  const remainingPageQueries = useQueries({
    queries: remainingPageParams.map((params) =>
      publicPropertyResource.createListQueryOptions(params, {
        enabled: firstPageQuery.data !== undefined,
      }),
    ),
  });

  const items = useMemo(() => {
    const combined = [
      ...(firstPageQuery.data?.items ?? []),
      ...remainingPageQueries.flatMap((query) => query.data?.items ?? []),
    ];

    if (combined.length <= 1) {
      return combined;
    }

    const seen = new Set<string>();
    return combined.filter((entry) => {
      if (typeof entry.id !== 'string' || entry.id.length === 0) {
        return true;
      }
      if (seen.has(entry.id)) {
        return false;
      }
      seen.add(entry.id);
      return true;
    });
  }, [firstPageQuery.data?.items, remainingPageQueries]);

  const data = useMemo(() => {
    const base = firstPageQuery.data;
    if (!base) {
      return undefined;
    }

    return {
      ...base,
      items,
    };
  }, [firstPageQuery.data, items]);

  const error =
    firstPageQuery.error ?? remainingPageQueries.find((query) => query.error)?.error ?? null;
  const isFetching =
    firstPageQuery.isFetching || remainingPageQueries.some((query) => query.isFetching);

  return {
    data,
    isLoading: firstPageQuery.isLoading,
    isFetching,
    error,
  };
}

export type UsePublicPropertyOptions = Omit<GetPublicPropertyParams, 'signal'>;

export function usePublicProperty(
  options: UsePublicPropertyOptions | null,
): UseQueryResult<PublicPropertyDetail> {
  const identifier = typeof options?.identifier === 'string' ? options.identifier.trim() : '';
  const normalizedIdentifier = identifier.length > 0 ? identifier : null;
  const queryClient = useQueryClient();
  const detailParams = useMemo(() => {
    if (normalizedIdentifier === null) {
      return null;
    }
    return { identifier: normalizedIdentifier } as const;
  }, [normalizedIdentifier]);

  const queryResult = publicPropertyResource.useDetail(detailParams ?? { identifier: '' }, {
    enabled: detailParams !== null,
  });

  const propertyId = queryResult.data?.summary.id ?? null;

  const detailSubscribe = useMemo(() => {
    if (detailParams === null || propertyId === null) {
      return undefined;
    }

    const subscribe = createSupabaseChannelSubscribe<{ identifier: string }>({
      supabase,
      channel: `public-property-${propertyId}`,
      table: 'units',
      event: '*',
      filter: `property_id=eq.${propertyId}`,
      mapQueryKeys: (params) => [publicPropertyResource.keys.detail(params)],
    });

    return () => subscribe({ params: detailParams, queryClient });
  }, [detailParams, propertyId, queryClient]);

  publicPropertyResource.useRealtimeInvalidation(
    detailParams ?? { identifier: '' },
    detailSubscribe,
  );

  return queryResult;
}
